import type {
  AgeCategory,
  ClientDTO,
  ClientTitle,
  ClientType,
  CreateClientRequest,
  Language,
} from '@brainkey/shared';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { errorMessage, normalizePhone } from '../lib/format';
import { useCreateClient } from '../lib/hooks/useClients';

import {
  AddContactButton,
  ClientContactFields,
  EMPTY_CONTACT,
  isContactRowFilled,
} from './ClientContactFields';
import type { ContactFormValue } from './ClientContactFields';
import { Alert } from './ui/Alert';
import { Button } from './ui/Button';
import { ClientCodeField } from './ui/ClientCodeField';
import { CountryField } from './ui/CountryField';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';
import { PhoneNumberField } from './ui/PhoneNumberField';
import { Select } from './ui/Select';

const TITLE_OPTIONS: { value: ClientTitle | ''; label: string }[] = [
  { value: '', label: 'Not specified' },
  { value: 'MR', label: 'Mr' },
  { value: 'MRS', label: 'Mrs' },
  { value: 'MS', label: 'Ms' },
  { value: 'MISS', label: 'Miss' },
  { value: 'DR', label: 'Dr' },
  { value: 'REV', label: 'Rev' },
  { value: 'PROF', label: 'Prof' },
];

interface CreateClientModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (client: ClientDTO) => void;
  /** Prefills step 1/2 — used by the Leads "Convert to client" flow so the
   * coach doesn't retype what the prospect already submitted. */
  initial?: { firstName?: string; lastName?: string; email?: string; phone?: string } | undefined;
  /** Overrides the default `useCreateClient()` call — the Leads flow passes
   * one backed by `POST /leads/:id/convert` instead of a plain
   * `POST /clients`, so the lead is marked CONVERTED atomically
   * server-side rather than as a separate follow-up call. */
  submit?: ((body: CreateClientRequest) => Promise<ClientDTO>) | undefined;
  title?: string | undefined;
  description?: string | undefined;
  submitLabel?: string | undefined;
}

interface ContactRow extends ContactFormValue {
  key: number;
}

let nextRowKey = 0;

const EMPTY = {
  title: '' as ClientTitle | '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  secondaryPhone: '',
  languagePref: 'EN' as Language,
  ageCategory: '' as AgeCategory | '',
  clientType: '' as ClientType | '',
  clientCode: '',
  country: '',
  city: '',
  contacts: [] as ContactRow[],
};

export function CreateClientModal({
  open,
  onClose,
  onCreated,
  initial,
  submit,
  title = 'Create New Client',
  description = 'A quick 4-step setup — name, contact details, preferences, and family contacts.',
  submitLabel = 'Complete Setup',
}: CreateClientModalProps): JSX.Element {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const createClient = useCreateClient();
  const submitClient = submit ?? ((body: CreateClientRequest) => createClient.mutateAsync(body));

  // Re-seed from `initial` every time the modal opens (not just on first
  // mount) — this component stays mounted with `open` toggling, and the
  // Leads flow reuses one instance across different leads.
  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, ...initial });
      setError(null);
      setStep(1);
    }
    // Deliberately keyed on `open` alone: `initial` is a fresh object
    // literal from the caller on every render, so including it would
    // re-run (and reset the form) on every keystroke instead of only
    // when the modal opens. (Matches this codebase's existing convention
    // of leaving exhaustive-deps as an unaddressed warning rather than
    // suppressing it — see GrapesJsEditor.tsx's own useEffect.)
  }, [open]);

  const set = <K extends keyof typeof EMPTY>(key: K, value: (typeof EMPTY)[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const reset = (): void => {
    setForm(EMPTY);
    setError(null);
    setStep(1);
  };

  const handleClose = (): void => {
    if (submitting) return;
    reset();
    onClose();
  };

  const nextStep = () => {
    if (step === 1 && (!form.firstName || !form.lastName)) {
      setError('Please fill in the required fields.');
      return;
    }
    if (step === 3 && (form.clientType === '' || form.clientCode.trim() === '')) {
      setError('Please choose a client type and enter a client ID.');
      return;
    }
    setError(null);
    setStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setError(null);
    setStep((prev) => prev - 1);
  };

  const addContactRow = (): void => {
    nextRowKey += 1;
    setForm((prev) => ({
      ...prev,
      contacts: [...prev.contacts, { ...EMPTY_CONTACT, key: nextRowKey }],
    }));
  };

  const updateContactRow = (key: number, next: ContactFormValue): void => {
    setForm((prev) => ({
      ...prev,
      contacts: prev.contacts.map((row) => (row.key === key ? { ...next, key } : row)),
    }));
  };

  const removeContactRow = (key: number): void => {
    setForm((prev) => ({ ...prev, contacts: prev.contacts.filter((row) => row.key !== key) }));
  };

  const isRowEmpty = (v: ContactFormValue): boolean =>
    v.relatedClientId === null &&
    v.name.trim() === '' &&
    v.phone.trim() === '' &&
    v.email.trim() === '';

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    const incompleteRow = form.contacts.find((c) => !isRowEmpty(c) && !isContactRowFilled(c));
    if (incompleteRow) {
      setError('Each contact needs a name and at least a phone or email — or remove it.');
      return;
    }

    if (form.clientType === '' || form.clientCode.trim() === '') {
      setError('Please choose a client type and enter a client ID.');
      return;
    }

    const body: CreateClientRequest = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      languagePref: form.languagePref,
      clientType: form.clientType,
      clientCode: form.clientCode.trim(),
    };
    if (form.title !== '') body.title = form.title;
    if (form.email.trim() !== '') body.email = form.email.trim();
    if (form.phone.trim() !== '') body.phone = normalizePhone(form.phone);
    if (form.secondaryPhone.trim() !== '')
      body.secondaryPhone = normalizePhone(form.secondaryPhone);
    if (form.ageCategory !== '') body.ageCategory = form.ageCategory;
    if (form.country.trim() !== '') body.country = form.country.trim();
    if (form.city.trim() !== '') body.city = form.city.trim();

    const filledContacts = form.contacts.filter(isContactRowFilled);
    if (filledContacts.length > 0) {
      body.contacts = filledContacts.map((c) => ({
        relationType: c.relationType,
        name: c.name.trim(),
        ...(c.phone.trim() !== '' ? { phone: normalizePhone(c.phone) } : {}),
        ...(c.email.trim() !== '' ? { email: c.email.trim() } : {}),
        languagePref: c.languagePref,
        ...(c.relatedClientId !== null ? { relatedClientId: c.relatedClientId } : {}),
      }));
    }

    setSubmitting(true);
    try {
      const client = await submitClient(body);
      reset();
      onCreated(client);
    } catch (err) {
      setError(errorMessage(err, 'Could not create the client.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      description={description}
      footer={
        <div className="flex justify-between w-full items-center">
          <div className="flex gap-1.5">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`w-2 h-2 rounded-full transition-colors ${step === s ? 'bg-brand-500' : 'bg-zinc-200 dark:bg-zinc-700'}`}
              />
            ))}
          </div>
          <div className="flex gap-3">
            {step > 1 && (
              <Button variant="ghost" onClick={prevStep} disabled={submitting}>
                Back
              </Button>
            )}
            {step === 1 && (
              <Button variant="ghost" onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
            )}
            {step < 4 ? (
              <Button variant="primary" type="button" onClick={nextStep}>
                Continue
              </Button>
            ) : (
              <Button
                variant="primary"
                type="submit"
                form="create-client-form"
                loading={submitting}
              >
                {submitLabel}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <form id="create-client-form" onSubmit={(e) => void onSubmit(e)} noValidate>
        {error !== null && (
          <div className="mb-6">
            <Alert tone="danger">{error}</Alert>
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex flex-col gap-5"
            >
              <div className="mb-2">
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                  Personal Information
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Let&apos;s start with the client&apos;s basic details.
                </p>
              </div>
              <Select
                label="Title"
                optional
                value={form.title}
                onValueChange={(value) => set('title', value as ClientTitle | '')}
                options={TITLE_OPTIONS}
              />
              <Input
                label="First name"
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                required
                autoFocus
              />
              <Input
                label="Last name"
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                required
              />
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex flex-col gap-5"
            >
              <div className="mb-2">
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                  Contact Details
                </h3>
                <p className="text-xs text-zinc-500 mt-1">How can we reach this client?</p>
              </div>
              <Input
                label="Email"
                type="email"
                optional
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
              />
              <PhoneNumberField
                optional
                value={form.phone}
                onChange={(value) => set('phone', value)}
              />
              <PhoneNumberField
                label="Secondary Mobile"
                optional
                value={form.secondaryPhone}
                onChange={(value) => set('secondaryPhone', value)}
              />
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex flex-col gap-5"
            >
              <div className="mb-2">
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                  Preferences
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Language and demographic details for reporting.
                </p>
              </div>
              <Select
                label="Preferred language"
                value={form.languagePref}
                onValueChange={(value) => set('languagePref', value as Language)}
                options={[
                  { value: 'EN', label: 'English' },
                  { value: 'SI', label: 'Sinhala' },
                  { value: 'TA', label: 'Tamil' },
                ]}
              />
              <Select
                label="Age category"
                optional
                value={form.ageCategory}
                onValueChange={(value) => set('ageCategory', value as AgeCategory | '')}
                options={[
                  { value: '', label: 'Not specified' },
                  { value: 'CHILD', label: 'Child' },
                  { value: 'TEEN', label: 'Teen' },
                  { value: 'ADULT', label: 'Adult' },
                  { value: 'SENIOR', label: 'Senior' },
                ]}
              />
              <CountryField
                optional
                value={form.country}
                onChange={(value) => set('country', value)}
              />
              <Input
                label="City"
                optional
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
              />
              <Select
                label="Client type"
                value={form.clientType}
                onValueChange={(value) => set('clientType', value as ClientType | '')}
                options={[
                  { value: '', label: 'Select a client type…' },
                  { value: 'LOCAL', label: 'Local' },
                  { value: 'FOREIGN', label: 'Foreign' },
                ]}
              />
              <ClientCodeField
                label="Client ID"
                hint="A short business ID you assign, e.g. L-1234 or NRA-19834."
                value={form.clientCode}
                onChange={(value) => set('clientCode', value)}
              />
            </motion.div>
          )}

          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex flex-col gap-4"
            >
              <div className="mb-2">
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                  Family &amp; Relations
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Optional — add a parent/guardian if this client is a minor or needs a different
                  point of contact (e.g. a different preferred language).
                </p>
              </div>
              {form.contacts.map((row) => (
                <ClientContactFields
                  key={row.key}
                  value={row}
                  onChange={(next) => updateContactRow(row.key, next)}
                  onRemove={() => removeContactRow(row.key)}
                />
              ))}
              <div>
                <AddContactButton onClick={addContactRow} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </Modal>
  );
}
